import type { Division, Pack } from '@/lib/types/deck-builder';
import { UnitCategory } from '@/lib/types/deck-builder';
import type { Unit } from '@/lib/types/unit';
import { InfoPanelType, MovementType } from '@/lib/types/unit';
import type { WeaponData, MergedAmmoData } from '@/lib/types/weapon';
import type {
  DivisionProfile,
  DivisionBaseline,
  SlotEconomics,
  UnitCPSpread,
  NotableUnit,
  DistributionPoint,
  CategoryProfile,
  LogisticsProfile,
  InfantryProfile,
  TanksProfile,
  ReconProfile,
  AntiAirProfile,
  ArtilleryProfile,
  HelicopterProfile,
  PlanesProfile,
  PlaystyleOverview,
  HeliborneEntry,
} from '@/lib/types/division-profile';
import {
  GROUND_OPTICS_EXCEPTIONAL_MIN,
  GROUND_OPTICS_NEW_SCALE_MIN_GRU,
} from '@/lib/utils/optics-paliers';

/** In-game durability (`maxDamage`); gunships are 6+ HP. Use strict `<` so 6 counts (e.g. AH-1). */
const MIN_MAX_DAMAGE_FOR_GUNSHIP = 6;

type WeaponsData = {
  [descriptorName: string]: {
    descriptorName: string;
    hasDefensiveSmoke: boolean;
    weapons: WeaponData[];
  };
};
type AmmunitionData = { [descriptorName: string]: MergedAmmoData };

interface ResolvedUnit {
  unit: Unit;
  pack: Pack;
  weapons: WeaponData[];
}

function resolveWeaponDescriptor(
  weaponDescriptorName: string | undefined,
  weapons: WeaponsData
): WeaponData[] {
  if (!weaponDescriptorName) return [];
  const key = weaponDescriptorName.replace(/^\$\/GFX\/Weapon\//, '');
  const entry = weapons[key];
  return entry?.weapons ?? [];
}

function getSlotEconomics(
  division: Division,
  category: UnitCategory
): SlotEconomics {
  const row = division.costMatrix.matrix.find((r) => r.name === category);
  const costs = row?.activationCosts ?? [];
  return {
    totalSlots: costs.length,
    cheapSlots: costs.filter((c) => c === 1).length,
    midSlots: costs.filter((c) => c === 2).length,
    expensiveSlots: costs.filter((c) => c >= 3).length,
    activationCosts: costs,
  };
}

function getCPSpread(resolvedUnits: ResolvedUnit[]): UnitCPSpread {
  if (resolvedUnits.length === 0) {
    return { min: 0, max: 0, average: 0, unitCount: 0 };
  }
  const cps = resolvedUnits.map((ru) => ru.unit.commandPoints);
  const sum = cps.reduce((a, b) => a + b, 0);
  return {
    min: Math.min(...cps),
    max: Math.max(...cps),
    average: Math.round(sum / cps.length),
    unitCount: cps.length,
  };
}

function resolvePackUnits(
  packs: Pack[],
  units: Unit[],
  weapons: WeaponsData,
  category: UnitCategory
): ResolvedUnit[] {
  const unitMap = new Map<string, Unit>();
  for (const u of units) {
    unitMap.set(u.descriptorName, u);
  }

  const resolved: ResolvedUnit[] = [];
  for (const pack of packs) {
    const unit = unitMap.get(pack.unitDescriptor);
    if (!unit) continue;
    if (unit.factoryDescriptor !== category) continue;
    const weaponData = resolveWeaponDescriptor(
      unit.weaponDescriptorName,
      weapons
    );
    resolved.push({ unit, pack, weapons: weaponData });
  }
  return resolved;
}

function hasATGMWeapons(weapons: WeaponData[]): boolean {
  return weapons.some((w) => w.ammo.minMaxCategory === 'MinMax_ATGM');
}

function hasActiveProtectionWeapons(weapons: WeaponData[]): boolean {
  return weapons.some((w) => w.ammo.traits?.includes('activeprotection'));
}

/** Max howitzer-shell HE (`MinMax_obusier`) for a unit's weapons. */
function getMaxHeHowitzerDamage(weapons: WeaponData[]): number {
  let max = 0;
  for (const w of weapons) {
    if (w.ammo.minMaxCategory !== 'MinMax_obusier') continue;
    const he = w.ammo.heDamage ?? 0;
    if (he > max) max = he;
  }
  return max;
}

const HOWITZER_CALIBER_MM = /\b(\d{2,3})mm\b/i;

/** Largest caliber in mm from howitzer tube ammo names (e.g. 152mm, 203mm). */
function getMaxHowitzerCaliberMm(weapons: WeaponData[]): number {
  let max = 0;
  for (const w of weapons) {
    if (w.ammo.minMaxCategory !== 'MinMax_obusier') continue;
    for (const text of [
      w.ammo.name,
      w.ammo.descriptorName,
      ...(w.ammoDescriptors ?? []),
    ]) {
      const m = text.match(HOWITZER_CALIBER_MM);
      if (!m?.[1]) continue;
      const n = parseInt(m[1], 10);
      if (n >= 75 && n <= 406 && n > max) max = n;
    }
  }
  return max;
}

/** Tube autocannon, chaingun/gatling (M197 Cobra), or AFV-style cannon on a helo. */
const GUNSHIP_CANNON_MIN_MAX = new Set<string>([
  'MinMax_AutocanonHE',
  'MinMax_CanonAP',
  'MinMax_Gatling',
]);

function hasHeloAutocannonWeapon(weapons: WeaponData[]): boolean {
  return weapons.some((w) => GUNSHIP_CANNON_MIN_MAX.has(w.ammo.minMaxCategory));
}

function hasGunshipSecondaryOrdnance(weapons: WeaponData[]): boolean {
  return weapons.some((w) => {
    const c = w.ammo.minMaxCategory;
    return (
      c === 'MinMax_Roquette_AS' ||
      c === 'MinMax_ATGM' ||
      /** Hellfire-class helio AT; data uses `MinMax_AGM` not `MinMax_ATGM` (e.g. AH-64 ATAS). */
      c === 'MinMax_AGM' ||
      c === 'MinMax_AAM'
    );
  });
}

/** Mikhail-style gunship: durable heli + autocannon/gatling + rockets/ATGM/AAM. */
function isGunship(unit: Unit, weapons: WeaponData[]): boolean {
  if (unit.movementType !== MovementType.HELICOPTER) return false;
  if (
    unit.infoPanelType === InfoPanelType.TRANSPORT_HELICOPTER ||
    unit.infoPanelType === InfoPanelType.SUPPLY_HELICOPTER
  )
    return false;
  if ((unit.maxDamage ?? 0) < MIN_MAX_DAMAGE_FOR_GUNSHIP) return false;
  if (!hasHeloAutocannonWeapon(weapons)) return false;
  return hasGunshipSecondaryOrdnance(weapons);
}

function hasAaHeliMissile(weapons: WeaponData[]): boolean {
  return weapons.some((w) => {
    const c = w.ammo.minMaxCategory;
    return c === 'MinMax_AAM' || c === 'MinMax_SAM';
  });
}

function maxAsfAamPlaneRange(weapons: WeaponData[]): number {
  if (!isASFLoadout(weapons)) return 0;
  let max = 0;
  for (const w of weapons) {
    if (w.ammo.minMaxCategory !== 'MinMax_AAM') continue;
    const r = w.ammo.planeMaxRange ?? 0;
    if (r > max) max = r;
  }
  return max;
}

/** Categories that use `planeMaxRange` for anti-air missiles (AAM + radar/long SAM). */
const AA_MISSILE_MIN_MAX = new Set<string>(['MinMax_AAM', 'MinMax_SAM']);

/** Distinct plane-engagement ranges from all AA-class missiles in the dataset. */
function buildAamPlaneRangeBenchmark(weapons: WeaponsData): number[] {
  const set = new Set<number>();
  for (const entry of Object.values(weapons)) {
    for (const w of entry.weapons ?? []) {
      if (!AA_MISSILE_MIN_MAX.has(w.ammo.minMaxCategory)) continue;
      const r = w.ammo.planeMaxRange ?? 0;
      if (r > 0) set.add(r);
    }
  }
  return [...set].sort((a, b) => a - b);
}

function divisionHasSigint(division: Division, units: Unit[]): boolean {
  const unitMap = new Map(units.map((u) => [u.descriptorName, u]));
  for (const pack of division.packs) {
    const u = unitMap.get(pack.unitDescriptor);
    if (u?.specialities?.includes('_singint')) return true;
  }
  return false;
}

function isHelicopterTransportForInfantry(unit: Unit): boolean {
  if (unit.movementType !== MovementType.HELICOPTER) return false;
  if (unit.infoPanelType === InfoPanelType.SUPPLY_HELICOPTER) return false;
  return true;
}

function hasRadarWeapons(weapons: WeaponData[]): boolean {
  return weapons.some(
    (w) => w.ammo.traits && w.ammo.traits.includes('RADAR')
  );
}

/** Exceptional spotter: top ground optics tier only (raw GRU / legacy rescale band). */
function countsAsExceptionalOptics(unit: Unit): boolean {
  const o = unit.optics;
  if (o >= GROUND_OPTICS_NEW_SCALE_MIN_GRU) {
    return o >= GROUND_OPTICS_EXCEPTIONAL_MIN;
  }
  return o >= 233;
}

function getMaxPenetration(weapons: WeaponData[]): number {
  if (weapons.length === 0) return 0;
  return Math.max(...weapons.map((w) => w.ammo.penetration ?? 0));
}

function getMainGun(weapons: WeaponData[]): WeaponData | undefined {
  if (weapons.length === 0) return undefined;
  const nonAtgm = weapons.filter((w) => w.ammo.minMaxCategory !== 'MinMax_ATGM');
  const candidates = nonAtgm.length > 0 ? nonAtgm : weapons;
  return candidates.reduce((best, w) =>
    w.ammo.penetration > best.ammo.penetration ? w : best
  );
}

function getMainGunPenetration(weapons: WeaponData[]): number {
  return getMainGun(weapons)?.ammo.penetration ?? 0;
}

function getMainGunStaticAccuracy(weapons: WeaponData[]): number {
  return getMainGun(weapons)?.ammo.staticAccuracy ?? 0;
}

function getMaxRange(
  weapons: WeaponData[],
  rangeField: 'heliMaxRange' | 'planeMaxRange' | 'groundMaxRange'
): number {
  if (weapons.length === 0) return 0;
  return Math.max(...weapons.map((w) => w.ammo[rangeField] ?? 0));
}

const GROUND_ORDNANCE_CATEGORIES = new Set([
  'MinMax_Bombe', 'MinMax_Cluster', 'MinMax_AGM',
  'MinMax_SEAD', 'MinMax_Roquette_AS', 'MinMax_MLRS',
]);

function isASFLoadout(weapons: WeaponData[]): boolean {
  if (weapons.length === 0) return false;
  const hasAAM = weapons.some((w) => w.ammo.minMaxCategory === 'MinMax_AAM');
  if (!hasAAM) return false;
  return !weapons.some((w) => GROUND_ORDNANCE_CATEGORIES.has(w.ammo.minMaxCategory));
}

function hasSEADWeapons(weapons: WeaponData[]): boolean {
  return weapons.some((w) => w.ammo.minMaxCategory === 'MinMax_SEAD');
}

function isAttackHeli(unit: Unit, weapons: WeaponData[]): boolean {
  if (
    unit.infoPanelType === InfoPanelType.TRANSPORT_HELICOPTER ||
    unit.infoPanelType === InfoPanelType.SUPPLY_HELICOPTER
  )
    return false;
  return weapons.some(
    (w) => w.ammo.penetration > 0 || w.ammo.heDamage > 0
  );
}

/** Armed attack heli excluding gunships — disjoint KPI from `isGunship`. */
function isLightAttackHeli(unit: Unit, weapons: WeaponData[]): boolean {
  return isAttackHeli(unit, weapons) && !isGunship(unit, weapons);
}

function makeNotableUnit(
  unit: Unit,
  value: number,
  cards: number = 1
): NotableUnit {
  return {
    name: unit.name,
    descriptorName: unit.descriptorName,
    value,
    cards,
  };
}

function buildDistribution(
  resolved: ResolvedUnit[],
  getValue: (ru: ResolvedUnit) => number
): DistributionPoint[] {
  return resolved
    .map((ru) => ({ name: ru.unit.name, value: getValue(ru) }))
    .sort((a, b) => b.value - a.value);
}

const ARMOR_HISTOGRAM_BIN_ORDER = [
  '≤9 FA',
  '10–12 FA',
  '13–15 FA',
  '16–18 FA',
  '19+ FA',
] as const;

function frontArmorBinLabel(fa: number): string {
  if (fa <= 9) return '≤9 FA';
  if (fa <= 12) return '10–12 FA';
  if (fa <= 15) return '13–15 FA';
  if (fa <= 18) return '16–18 FA';
  return '19+ FA';
}

function buildArmorHistogram(resolved: ResolvedUnit[]): DistributionPoint[] {
  const counts = new Map<string, number>();
  for (const ru of resolved) {
    const bin = frontArmorBinLabel(ru.unit.frontArmor);
    const w = ru.pack.numberOfCards * ru.pack.numberOfUnitsInPack;
    counts.set(bin, (counts.get(bin) ?? 0) + w);
  }
  const result: DistributionPoint[] = [];
  for (const name of ARMOR_HISTOGRAM_BIN_ORDER) {
    const value = counts.get(name) ?? 0;
    if (value > 0) result.push({ name, value });
  }
  return result;
}

function baseCategoryProfile(
  category: UnitCategory,
  division: Division,
  resolved: ResolvedUnit[]
): CategoryProfile {
  return {
    category,
    slots: getSlotEconomics(division, category),
    cpSpread: getCPSpread(resolved),
    packCount: resolved.length,
  };
}

function isDeckCommandUnit(unit: Unit): boolean {
  if (unit.isCommand === true) return true;
  return unit.specialities?.includes('_leader') ?? false;
}

function analyzeLogistics(
  division: Division,
  units: Unit[],
  weapons: WeaponsData
): LogisticsProfile {
  const resolved = resolvePackUnits(
    division.packs,
    units,
    weapons,
    UnitCategory.LOG
  );
  const base = baseCategoryProfile(UnitCategory.LOG, division, resolved);

  let supplyUnitCount = 0;
  let totalSupplyCapacity = 0;
  let fobCount = 0;
  let hasSupplyHeli = false;
  let supplyHeliName: string | undefined;
  let cvCount = 0;
  const cvUnits: NotableUnit[] = [];
  let fwdCvCount = 0;
  const fwdCvUnits: NotableUnit[] = [];

  for (const ru of resolved) {
    const panel = ru.unit.infoPanelType;
    const cards = ru.pack.numberOfCards;
    if (isDeckCommandUnit(ru.unit)) {
      cvCount += cards;
      cvUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
    }
    if (
      isDeckCommandUnit(ru.unit) &&
      ru.unit.advancedDeployment > 0
    ) {
      fwdCvCount += cards;
      fwdCvUnits.push(
        makeNotableUnit(ru.unit, ru.unit.commandPoints, cards)
      );
    }
    if (
      panel === InfoPanelType.SUPPLY_VEHICLE ||
      panel === InfoPanelType.SUPPLY_HELICOPTER ||
      panel === InfoPanelType.FOB
    ) {
      supplyUnitCount++;
      totalSupplyCapacity += ru.unit.supply ?? 0;
    }
    if (panel === InfoPanelType.FOB) fobCount++;
    if (panel === InfoPanelType.SUPPLY_HELICOPTER) {
      hasSupplyHeli = true;
      supplyHeliName = ru.unit.name;
    }
  }

  const supplyDistribution = resolved
    .filter((ru) => {
      const p = ru.unit.infoPanelType;
      return (
        p === InfoPanelType.SUPPLY_VEHICLE ||
        p === InfoPanelType.SUPPLY_HELICOPTER ||
        p === InfoPanelType.FOB
      );
    })
    .map((ru) => ({ name: ru.unit.name, value: ru.unit.supply ?? 0 }))
    .sort((a, b) => b.value - a.value);

  return {
    ...base,
    category: UnitCategory.LOG,
    supplyUnitCount,
    totalSupplyCapacity,
    fobCount,
    hasSupplyHeli,
    supplyHeliName,
    cvCount,
    cvUnits,
    fwdCvCount,
    fwdCvUnits,
    supplyDistribution,
  };
}

function analyzeInfantry(
  division: Division,
  units: Unit[],
  weapons: WeaponsData
): InfantryProfile {
  const resolved = resolvePackUnits(
    division.packs,
    units,
    weapons,
    UnitCategory.INF
  );
  const base = baseCategoryProfile(UnitCategory.INF, division, resolved);

  const unitMap = new Map<string, Unit>();
  for (const u of units) unitMap.set(u.descriptorName, u);

  const infSlots = getSlotEconomics(division, UnitCategory.INF).totalSlots;
  const fieldableInfModels = resolved
    .map((ru) => ru.pack.numberOfCards * ru.pack.numberOfUnitsInPack)
    .sort((a, b) => b - a)
    .slice(0, infSlots)
    .reduce((sum, v) => sum + v, 0);

  let fwdInfCount = 0;
  const fwdInfUnits: NotableUnit[] = [];
  let eliteSfCount = 0;
  const eliteSfUnits: NotableUnit[] = [];
  let atmgSquadCount = 0;
  const atmgSquadUnits: NotableUnit[] = [];
  let atmgInfCount = 0;
  const atmgInfUnits: NotableUnit[] = [];
  let highestInfATPen = 0;
  let highestInfATUnit: NotableUnit | undefined;
  let fwdATInfCount = 0;
  const fwdATInfUnits: NotableUnit[] = [];
  let ifvCount = 0;
  const ifvUnits: NotableUnit[] = [];
  let cvCount = 0;
  const cvUnits: NotableUnit[] = [];
  const seenIfvDescriptors = new Set<string>();
  let heliborneCardCount = 0;
  const heliborneEntries: HeliborneEntry[] = [];

  for (const ru of resolved) {
    const cards = ru.pack.numberOfCards;
    const pen = getMaxPenetration(ru.weapons);

    if (isDeckCommandUnit(ru.unit)) {
      cvCount += cards;
      cvUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
    }

    if (ru.unit.advancedDeployment > 0) {
      fwdInfCount += cards;
      fwdInfUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
      if (hasATGMWeapons(ru.weapons)) {
        fwdATInfCount += cards;
        fwdATInfUnits.push(makeNotableUnit(ru.unit, pen, cards));
      }
    }
    if (ru.unit.isSpecialForces) {
      eliteSfCount += cards;
      eliteSfUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
    }
    if (hasATGMWeapons(ru.weapons)) {
      if (ru.unit.specialities?.[0] === 'AT') {
        atmgSquadCount += cards;
        atmgSquadUnits.push(makeNotableUnit(ru.unit, pen, cards));
      } else {
        atmgInfCount += cards;
        atmgInfUnits.push(makeNotableUnit(ru.unit, pen, cards));
      }
    }

    if (pen > highestInfATPen) {
      highestInfATPen = pen;
      highestInfATUnit = makeNotableUnit(ru.unit, pen);
    }

    const heliTransportDescriptors: string[] = [];
    const heliTransportNames: string[] = [];
    const heliSeen = new Set<string>();
    for (const transportDesc of ru.pack.availableTransportList ?? []) {
      const transport = unitMap.get(transportDesc);
      if (transport && isHelicopterTransportForInfantry(transport) && !heliSeen.has(transportDesc)) {
        heliSeen.add(transportDesc);
        heliTransportDescriptors.push(transport.descriptorName);
        heliTransportNames.push(transport.name);
      }
    }
    if (heliTransportDescriptors.length > 0) {
      heliborneCardCount += cards;
      heliborneEntries.push({
        infantryName: ru.unit.name,
        infantryDescriptor: ru.unit.descriptorName,
        helicopterDescriptors: heliTransportDescriptors,
        helicopterNames: heliTransportNames,
        cards,
      });
    }

    for (const transportDesc of ru.pack.availableTransportList ?? []) {
      if (seenIfvDescriptors.has(transportDesc)) continue;
      const transport = unitMap.get(transportDesc);
      if (!transport) continue;
      const transportWeapons = resolveWeaponDescriptor(
        transport.weaponDescriptorName,
        weapons
      );
      const transportPen = getMaxPenetration(transportWeapons);
      if (transportPen >= 5) {
        seenIfvDescriptors.add(transportDesc);
        ifvCount++;
        ifvUnits.push(makeNotableUnit(transport, transportPen));
      }
    }
  }

  heliborneEntries.sort(
    (a, b) => b.cards - a.cards || a.infantryName.localeCompare(b.infantryName)
  );

  const cpDistribution = buildDistribution(resolved, (ru) => ru.unit.commandPoints);

  return {
    ...base,
    category: UnitCategory.INF,
    fieldableInfModels,
    fwdInfCount,
    fwdInfUnits,
    eliteSfCount,
    eliteSfUnits,
    atmgSquadCount,
    atmgSquadUnits,
    atmgInfCount,
    atmgInfUnits,
    highestInfATPen,
    highestInfATUnit,
    fwdATInfCount,
    fwdATInfUnits,
    ifvCount,
    ifvUnits,
    cvCount,
    cvUnits,
    heliborneCardCount,
    heliborneEntries,
    cpDistribution,
  };
}

function analyzeTanks(
  division: Division,
  units: Unit[],
  weapons: WeaponsData
): TanksProfile {
  const resolved = resolvePackUnits(
    division.packs,
    units,
    weapons,
    UnitCategory.TNK
  );
  const base = baseCategoryProfile(UnitCategory.TNK, division, resolved);

  const tnkSlots = getSlotEconomics(division, UnitCategory.TNK).totalSlots;
  const fieldableModels = resolved
    .map((ru) => ru.pack.numberOfCards * ru.pack.numberOfUnitsInPack)
    .sort((a, b) => b - a)
    .slice(0, tnkSlots)
    .reduce((sum, v) => sum + v, 0);

  let heaviestTank: NotableUnit | undefined;
  let maxArmor = 0;
  let mbtModels = 0;
  let tankPenScore = 0;
  let tankArmorScore = 0;
  let tankAccuracyScore = 0;
  let atmgVehicleCount = 0;
  const atmgVehicleUnits: NotableUnit[] = [];
  let activeProtectionCardCount = 0;
  const activeProtectionUnits: NotableUnit[] = [];
  let cvCount = 0;
  const cvUnits: NotableUnit[] = [];

  for (const ru of resolved) {
    const cards = ru.pack.numberOfCards;
    if (isDeckCommandUnit(ru.unit)) {
      cvCount += cards;
      cvUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
    }
    if (hasActiveProtectionWeapons(ru.weapons)) {
      activeProtectionCardCount += cards;
      activeProtectionUnits.push(
        makeNotableUnit(ru.unit, ru.unit.frontArmor, cards)
      );
    }
    if (hasATGMWeapons(ru.weapons)) {
      atmgVehicleCount += cards;
      atmgVehicleUnits.push(makeNotableUnit(ru.unit, getMaxPenetration(ru.weapons), cards));
    }
    if (ru.unit.frontArmor > maxArmor) {
      maxArmor = ru.unit.frontArmor;
      heaviestTank = makeNotableUnit(ru.unit, ru.unit.frontArmor);
    }

    const pen = getMainGunPenetration(ru.weapons);
    if (ru.unit.frontArmor < 10 || pen < 10) continue;

    const models = ru.pack.numberOfUnitsInPack * ru.pack.numberOfCards;
    mbtModels += models;
    tankPenScore += models * pen;
    tankArmorScore += models * ru.unit.frontArmor;
    tankAccuracyScore += models * getMainGunStaticAccuracy(ru.weapons);
  }

  const armorDistribution = buildDistribution(resolved, (ru) => ru.unit.frontArmor);
  const armorHistogram = buildArmorHistogram(resolved);

  return {
    ...base,
    category: UnitCategory.TNK,
    heaviestTank,
    fieldableModels,
    mbtModels,
    tankPenScore,
    tankArmorScore,
    tankAccuracyScore,
    atmgVehicleCount,
    atmgVehicleUnits,
    activeProtectionCardCount,
    activeProtectionUnits,
    cvCount,
    cvUnits,
    armorDistribution,
    armorHistogram,
  };
}

function analyzeRecon(
  division: Division,
  units: Unit[],
  weapons: WeaponsData
): ReconProfile {
  const resolved = resolvePackUnits(
    division.packs,
    units,
    weapons,
    UnitCategory.REC
  );
  const base = baseCategoryProfile(UnitCategory.REC, division, resolved);

  let fwdReconInfCount = 0;
  const fwdReconInfUnits: NotableUnit[] = [];
  let hasReconHeli = false;
  let hasReconPlane = false;
  let bestOpticsValue = 0;
  let bestOpticsUnit: NotableUnit | undefined;
  let exceptionalOpticsCount = 0;
  const exceptionalOpticsUnits: NotableUnit[] = [];
  let hasJammer = false;
  let reconTankCount = 0;
  const reconTankUnits: NotableUnit[] = [];
  let reconAfvCount = 0;
  const reconAfvUnits: NotableUnit[] = [];

  for (const ru of resolved) {
    const cards = ru.pack.numberOfCards;

    if (
      ru.unit.infoPanelType === InfoPanelType.INFANTRY &&
      ru.unit.advancedDeployment > 0
    ) {
      fwdReconInfCount += cards;
      fwdReconInfUnits.push(makeNotableUnit(ru.unit, ru.unit.optics, cards));
    }

    if (ru.unit.movementType === MovementType.HELICOPTER) hasReconHeli = true;
    if (ru.unit.movementType === MovementType.PLANE) hasReconPlane = true;

    if (ru.unit.optics > bestOpticsValue) {
      bestOpticsValue = ru.unit.optics;
      bestOpticsUnit = makeNotableUnit(ru.unit, ru.unit.optics);
    }
    if (countsAsExceptionalOptics(ru.unit)) {
      exceptionalOpticsCount += cards;
      exceptionalOpticsUnits.push(makeNotableUnit(ru.unit, ru.unit.optics, cards));
    }
    if (ru.unit.specialities?.includes('_jammer')) hasJammer = true;
    if (
      ru.unit.infoPanelType === InfoPanelType.DEFAULT &&
      ru.unit.movementType === MovementType.LAND
    ) {
      const pen = getMainGunPenetration(ru.weapons);
      if (ru.unit.frontArmor >= 10 && pen >= 10) {
        reconTankCount += cards;
        reconTankUnits.push(makeNotableUnit(ru.unit, ru.unit.frontArmor, cards));
      } else {
        reconAfvCount += cards;
        reconAfvUnits.push(makeNotableUnit(ru.unit, ru.unit.frontArmor, cards));
      }
    }
  }

  const opticsDistribution = buildDistribution(resolved, (ru) => ru.unit.optics);

  return {
    ...base,
    category: UnitCategory.REC,
    fwdReconInfCount,
    fwdReconInfUnits,
    hasReconHeli,
    hasReconPlane,
    bestOpticsUnit,
    bestOpticsValue,
    exceptionalOpticsCount,
    exceptionalOpticsUnits,
    hasJammer,
    reconTankCount,
    reconTankUnits,
    reconAfvCount,
    reconAfvUnits,
    opticsDistribution,
  };
}

function analyzeAntiAir(
  division: Division,
  units: Unit[],
  weapons: WeaponsData
): AntiAirProfile {
  const resolved = resolvePackUnits(
    division.packs,
    units,
    weapons,
    UnitCategory.AA
  );
  const base = baseCategoryProfile(UnitCategory.AA, division, resolved);

  let radarAACount = 0;
  const radarAAUnits: NotableUnit[] = [];
  let fwdAACount = 0;
  const fwdAAUnits: NotableUnit[] = [];
  let maxHeliRange = 0;
  let maxPlaneRange = 0;
  let aaHeliCount = 0;
  const aaHeliUnits: NotableUnit[] = [];

  for (const ru of resolved) {
    const cards = ru.pack.numberOfCards;

    if (
      ru.unit.movementType === MovementType.HELICOPTER &&
      hasAaHeliMissile(ru.weapons)
    ) {
      aaHeliCount += cards;
      aaHeliUnits.push(
        makeNotableUnit(
          ru.unit,
          Math.max(
            getMaxRange(ru.weapons, 'heliMaxRange'),
            getMaxRange(ru.weapons, 'planeMaxRange')
          ),
          cards
        )
      );
    }

    if (hasRadarWeapons(ru.weapons)) {
      radarAACount += cards;
      radarAAUnits.push(makeNotableUnit(ru.unit, getMaxRange(ru.weapons, 'planeMaxRange'), cards));
    }
    if (ru.unit.advancedDeployment > 0) {
      fwdAACount += cards;
      fwdAAUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
    }
    const heliRange = getMaxRange(ru.weapons, 'heliMaxRange');
    const planeRange = getMaxRange(ru.weapons, 'planeMaxRange');
    if (heliRange > maxHeliRange) maxHeliRange = heliRange;
    if (planeRange > maxPlaneRange) maxPlaneRange = planeRange;
  }

  const rangeDistribution = resolved
    .map((ru) => ({
      name: ru.unit.name,
      value: Math.max(
        getMaxRange(ru.weapons, 'heliMaxRange'),
        getMaxRange(ru.weapons, 'planeMaxRange')
      ),
    }))
    .filter((p) => p.value > 0)
    .sort((a, b) => b.value - a.value);

  return {
    ...base,
    category: UnitCategory.AA,
    radarAACount,
    radarAAUnits,
    fwdAACount,
    fwdAAUnits,
    aaHeliCount,
    aaHeliUnits,
    maxHeliRange,
    maxPlaneRange,
    rangeDistribution,
  };
}

/** Artillery sub-type for KPIs — do not rely on `specialities[0]` (role may not be first). */
function classifyArtilleryRole(ru: ResolvedUnit): 'mortar' | 'howitzer' | 'mlrs' | 'other' {
  const specs = ru.unit.specialities ?? [];
  const ws = ru.weapons;

  if (
    ws.some((w) => w.ammo.minMaxCategory === 'MinMax_MLRS') ||
    specs.includes('mlrs')
  ) {
    return 'mlrs';
  }
  if (
    specs.includes('mortar') ||
    ws.some((w) => w.ammo.minMaxCategory === 'MinMax_Mortier')
  ) {
    return 'mortar';
  }
  if (
    specs.includes('howitzer') ||
    ws.some((w) => w.ammo.minMaxCategory === 'MinMax_obusier')
  ) {
    return 'howitzer';
  }
  return 'other';
}

function analyzeArtillery(
  division: Division,
  units: Unit[],
  weapons: WeaponsData
): ArtilleryProfile {
  const resolved = resolvePackUnits(
    division.packs,
    units,
    weapons,
    UnitCategory.ART
  );
  const base = baseCategoryProfile(UnitCategory.ART, division, resolved);

  let mortarCount = 0;
  let howitzerCount = 0;
  let rocketCount = 0;
  let otherCount = 0;
  let clusterMLRSCount = 0;
  let napalmMLRSCount = 0;
  let thermobaricCount = 0;
  let maxHeHowitzer = 0;
  let heaviestHowitzerMm = 0;

  for (const ru of resolved) {
    const role = classifyArtilleryRole(ru);
    if (role === 'mortar') mortarCount++;
    else if (role === 'howitzer') {
      howitzerCount++;
      const he = getMaxHeHowitzerDamage(ru.weapons);
      if (he > maxHeHowitzer) maxHeHowitzer = he;
      const mm = getMaxHowitzerCaliberMm(ru.weapons);
      if (mm > heaviestHowitzerMm) heaviestHowitzerMm = mm;
    } else if (role === 'mlrs') rocketCount++;
    else otherCount++;

    if (ru.weapons.some((w) => w.ammo.traits?.includes('cluster'))) clusterMLRSCount++;
    if (ru.weapons.some((w) => w.ammo.traits?.includes('napalm'))) napalmMLRSCount++;
    if (ru.weapons.some((w) => w.ammo.traits?.includes('thermobaric'))) thermobaricCount++;
  }

  const typeDistribution: DistributionPoint[] = [
    { name: 'Mortar', value: mortarCount },
    { name: 'Howitzer', value: howitzerCount },
    { name: 'MLRS', value: rocketCount },
    { name: 'Other', value: otherCount },
  ].filter((p) => p.value > 0);

  return {
    ...base,
    category: UnitCategory.ART,
    mortarCount,
    howitzerCount,
    rocketCount,
    otherCount,
    maxHeHowitzer,
    heaviestHowitzerMm,
    clusterMLRSCount,
    napalmMLRSCount,
    thermobaricCount,
    typeDistribution,
  };
}

function analyzeHelicopters(
  division: Division,
  units: Unit[],
  weapons: WeaponsData
): HelicopterProfile {
  const resolved = resolvePackUnits(
    division.packs,
    units,
    weapons,
    UnitCategory.HEL
  );
  const base = baseCategoryProfile(UnitCategory.HEL, division, resolved);

  let lightAttackHeliCount = 0;
  const lightAttackHeliUnits: NotableUnit[] = [];
  let gunshipCount = 0;
  const gunshipUnits: NotableUnit[] = [];
  let highestHeliATPen = 0;
  let highestHeliATUnit: NotableUnit | undefined;

  for (const ru of resolved) {
    if (isLightAttackHeli(ru.unit, ru.weapons)) {
      lightAttackHeliCount += ru.pack.numberOfCards;
      lightAttackHeliUnits.push(
        makeNotableUnit(ru.unit, getMaxPenetration(ru.weapons), ru.pack.numberOfCards)
      );
    }
    if (isGunship(ru.unit, ru.weapons)) {
      gunshipCount += ru.pack.numberOfCards;
      gunshipUnits.push(
        makeNotableUnit(ru.unit, getMaxPenetration(ru.weapons), ru.pack.numberOfCards)
      );
    }
    const pen = getMaxPenetration(ru.weapons);
    if (pen > highestHeliATPen) {
      highestHeliATPen = pen;
      highestHeliATUnit = makeNotableUnit(ru.unit, pen);
    }
  }

  const cpDistribution = buildDistribution(resolved, (ru) => ru.unit.commandPoints);

  return {
    ...base,
    category: UnitCategory.HEL,
    lightAttackHeliCount,
    lightAttackHeliUnits,
    gunshipCount,
    gunshipUnits,
    highestHeliATPen,
    highestHeliATUnit,
    cpDistribution,
  };
}

function analyzePlanes(
  division: Division,
  units: Unit[],
  weapons: WeaponsData
): PlanesProfile {
  const resolved = resolvePackUnits(
    division.packs,
    units,
    weapons,
    UnitCategory.AIR
  );
  const base = baseCategoryProfile(UnitCategory.AIR, division, resolved);

  let asfCount = 0;
  const asfUnits: NotableUnit[] = [];
  let seadCount = 0;
  const seadUnits: NotableUnit[] = [];
  let lgbCount = 0;
  const lgbUnits: NotableUnit[] = [];
  let bomberMultiroleCount = 0;
  const bomberMultiroleUnits: NotableUnit[] = [];
  let ewCount = 0;
  const ewUnits: NotableUnit[] = [];
  let maxAsfMissileRange = 0;

  for (const ru of resolved) {
    const cards = ru.pack.numberOfCards;
    const descriptorUpper = ru.unit.descriptorName.toUpperCase();
    const weaponDescUpper = (ru.unit.weaponDescriptorName ?? '').toUpperCase();

    if (ru.unit.specialities?.includes('_electronic_warfare')) {
      ewCount += cards;
      ewUnits.push(makeNotableUnit(ru.unit, 0, cards));
    }

    if (isASFLoadout(ru.weapons)) {
      asfCount++;
      asfUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
      const aamR = maxAsfAamPlaneRange(ru.weapons);
      if (aamR > maxAsfMissileRange) maxAsfMissileRange = aamR;
    } else if (hasSEADWeapons(ru.weapons)) {
      seadCount++;
      seadUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
    } else if (
      descriptorUpper.includes('LGB') ||
      weaponDescUpper.includes('LGB')
    ) {
      lgbCount++;
      lgbUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
    } else if (!ru.unit.specialities?.includes('_electronic_warfare')) {
      /** EW-only airframes (e.g. EF-101 Voodoo, EF-111) often have no strike loadout — avoid counting them as Bomber/Multi too. */
      bomberMultiroleCount++;
      bomberMultiroleUnits.push(makeNotableUnit(ru.unit, ru.unit.commandPoints, cards));
    }
  }

  const typeDistribution: DistributionPoint[] = [
    { name: 'ASF', value: asfCount },
    { name: 'SEAD', value: seadCount },
    { name: 'LGB', value: lgbCount },
    { name: 'EW', value: ewCount },
    { name: 'Bomber/Multi', value: bomberMultiroleCount },
  ].filter((p) => p.value > 0);

  return {
    ...base,
    category: UnitCategory.AIR,
    asfCount,
    asfUnits,
    seadCount,
    seadUnits,
    lgbCount,
    lgbUnits,
    bomberMultiroleCount,
    bomberMultiroleUnits,
    ewCount,
    ewUnits,
    maxAsfMissileRange,
    typeDistribution,
  };
}

const DIVISION_TYPE_LABELS: Record<string, string> = {
  ARMORED: 'Armor',
  ARMORREC: 'Armored Recon',
  MECHANIZ: 'Mechanized',
  MOTORIZD: 'Motorized',
  AIRBORNE: 'Airborne',
  AIRMOBIL: 'Air Mobile',
  INFANREG: 'Infantry',
  DIVNAVAL: 'Naval',
};

function getDivisionTypeLabel(tags: string[]): string {
  for (const tag of tags) {
    const label = DIVISION_TYPE_LABELS[tag];
    if (label) return label;
  }
  return 'General Purpose';
}

function find30PenATUnits(
  division: Division,
  units: Unit[],
  weapons: WeaponsData,
  categories: UnitCategory[]
): NotableUnit[] {
  const result: NotableUnit[] = [];
  const seen = new Set<string>();
  for (const cat of categories) {
    const resolved = resolvePackUnits(division.packs, units, weapons, cat);
    for (const ru of resolved) {
      if (seen.has(ru.unit.descriptorName)) continue;
      const pen = getMaxPenetration(ru.weapons);
      if (pen >= 30) {
        seen.add(ru.unit.descriptorName);
        result.push(makeNotableUnit(ru.unit, pen, ru.pack.numberOfCards));
      }
    }
  }
  return result;
}

export function analyzeDivision(
  division: Division,
  units: Unit[],
  weapons: WeaponsData,
  _ammunition: AmmunitionData
): DivisionProfile {
  const logistics = analyzeLogistics(division, units, weapons);
  const infantry = analyzeInfantry(division, units, weapons);
  const tanks = analyzeTanks(division, units, weapons);
  const recon = analyzeRecon(division, units, weapons);
  const antiAir = analyzeAntiAir(division, units, weapons);
  const artillery = analyzeArtillery(division, units, weapons);
  const helicopters = analyzeHelicopters(division, units, weapons);
  const planes = analyzePlanes(division, units, weapons);

  const categoryProfiles = {
    logistics,
    infantry,
    tanks,
    recon,
    antiAir,
    artillery,
    helicopters,
    planes,
  };

  const thirtyPenATUnits = find30PenATUnits(division, units, weapons, [
    UnitCategory.TNK, UnitCategory.REC, UnitCategory.HEL,
    UnitCategory.AIR, UnitCategory.INF, UnitCategory.ART,
  ]);

  const overview: PlaystyleOverview = {
    maxActivationPoints: division.maxActivationPoints,
    totalPackCount: division.packs.length,
    playstyleTag: getDivisionTypeLabel(division.tags),
    has30PenAT: thirtyPenATUnits.length > 0,
    thirtyPenATUnits,
    hasSigint: divisionHasSigint(division, units),
  };

  return {
    divisionDescriptor: division.descriptor,
    divisionName: division.name,
    alliance: division.alliance,
    country: division.country,
    overview,
    ...categoryProfiles,
  };
}

export function computeDivisionBaseline(
  allProfiles: DivisionProfile[],
  weaponsForAamBenchmark?: WeaponsData | null
): DivisionBaseline {
  const aamPlaneRangeBenchmark = weaponsForAamBenchmark
    ? buildAamPlaneRangeBenchmark(weaponsForAamBenchmark)
    : undefined;

  if (allProfiles.length === 0) {
    return {
      sortedValues: {},
      binaryRates: {},
      ...(aamPlaneRangeBenchmark?.length ? { aamPlaneRangeBenchmark } : {}),
    };
  }

  const numericExtractors: Record<string, (p: DivisionProfile) => number> = {
    infSlots: (p) => p.infantry.slots.totalSlots,
    infFieldableModels: (p) => p.infantry.fieldableInfModels,
    sfCount: (p) => p.infantry.eliteSfCount,
    fwdInfCount: (p) => p.infantry.fwdInfCount,
    atmgSquadCount: (p) => p.infantry.atmgSquadCount,
    atmgInfCount: (p) => p.infantry.atmgInfCount,
    highestInfATPen: (p) => p.infantry.highestInfATPen,
    fwdATInfCount: (p) => p.infantry.fwdATInfCount,
    heliborneCardCount: (p) => p.infantry.heliborneCardCount,
    tnkSlots: (p) => p.tanks.slots.totalSlots,
    tankFieldableModels: (p) => p.tanks.fieldableModels,
    tankMbtModels: (p) => p.tanks.mbtModels,
    tankAvgPen: (p) => p.tanks.mbtModels > 0 ? p.tanks.tankPenScore / p.tanks.mbtModels : 0,
    tankAvgArmor: (p) => p.tanks.mbtModels > 0 ? p.tanks.tankArmorScore / p.tanks.mbtModels : 0,
    tankAvgAcc: (p) => p.tanks.mbtModels > 0 ? p.tanks.tankAccuracyScore / p.tanks.mbtModels : 0,
    reconExceptionalOptics: (p) => p.recon.exceptionalOpticsCount,
    reconTankCount: (p) => p.recon.reconTankCount,
    reconAfvCount: (p) => p.recon.reconAfvCount,
    radarAACount: (p) => p.antiAir.radarAACount,
    maxPlaneRange: (p) => p.antiAir.maxPlaneRange,
    artSlots: (p) => p.artillery.slots.totalSlots,
    lightAttackHeliCount: (p) => p.helicopters.lightAttackHeliCount,
    highestHeliATPen: (p) => p.helicopters.highestHeliATPen,
    gunshipCount: (p) => p.helicopters.gunshipCount,
    heliPackCount: (p) => p.helicopters.packCount,
    airSlots: (p) => p.planes.slots.totalSlots,
    planePackCount: (p) => p.planes.packCount,
    maxAsfMissileRange: (p) => p.planes.maxAsfMissileRange,
  };

  const sortedValues: Record<string, number[]> = {};
  for (const [key, extract] of Object.entries(numericExtractors)) {
    sortedValues[key] = allProfiles.map(extract).sort((a, b) => a - b);
  }

  // Derive composite tankScore: 75% average MBT quality + 25% quantity (sqrt for diminishing returns)
  const MIN_MBT_MODELS = 20;
  const maxMbtModels = Math.max(...allProfiles.map((p) => p.tanks.mbtModels), 1);
  const maxAvgPen    = Math.max(...allProfiles.map((p) => p.tanks.mbtModels > 0 ? p.tanks.tankPenScore / p.tanks.mbtModels : 0), 1);
  const maxAvgArmor  = Math.max(...allProfiles.map((p) => p.tanks.mbtModels > 0 ? p.tanks.tankArmorScore / p.tanks.mbtModels : 0), 1);
  const maxAvgAcc    = Math.max(...allProfiles.map((p) => p.tanks.mbtModels > 0 ? p.tanks.tankAccuracyScore / p.tanks.mbtModels : 0), 1);
  sortedValues['tankScore'] = allProfiles.map((p) => {
    if (p.tanks.mbtModels < MIN_MBT_MODELS) return 0;
    const avgPen   = p.tanks.tankPenScore / p.tanks.mbtModels;
    const avgArmor = p.tanks.tankArmorScore / p.tanks.mbtModels;
    const avgAcc   = p.tanks.tankAccuracyScore / p.tanks.mbtModels;
    const quality  = (avgPen / maxAvgPen) + (avgArmor / maxAvgArmor) + (avgAcc / maxAvgAcc);
    const qty      = Math.sqrt(p.tanks.mbtModels / maxMbtModels);
    return quality * 0.75 + qty * 0.75;
  }).sort((a, b) => a - b);

  const total = allProfiles.length;
  const binaryRates: Record<string, number> = {
    hasIFV: allProfiles.filter((p) => p.infantry.ifvCount > 0).length / total,
    hasJammer: allProfiles.filter((p) => p.recon.hasJammer).length / total,
    hasSupplyHeli: allProfiles.filter((p) => p.logistics.hasSupplyHeli).length / total,
    hasReconPlane: allProfiles.filter((p) => p.recon.hasReconPlane).length / total,
    hasReconTank: allProfiles.filter((p) => p.recon.reconTankCount > 0).length / total,
    has30PenAT: allProfiles.filter((p) => p.overview.has30PenAT).length / total,
    hasReconHeli: allProfiles.filter((p) => p.recon.hasReconHeli).length / total,
    hasASF: allProfiles.filter((p) => p.planes.asfCount > 0).length / total,
    hasSEAD: allProfiles.filter((p) => p.planes.seadCount > 0).length / total,
    hasLGB: allProfiles.filter((p) => p.planes.lgbCount > 0).length / total,
    hasEW: allProfiles.filter((p) => p.planes.ewCount > 0).length / total,
    hasCluster: allProfiles.filter((p) => p.artillery.clusterMLRSCount > 0).length / total,
    hasNapalm: allProfiles.filter((p) => p.artillery.napalmMLRSCount > 0).length / total,
    hasThermobaric: allProfiles.filter((p) => p.artillery.thermobaricCount > 0).length / total,
    hasSigint: allProfiles.filter((p) => p.overview.hasSigint).length / total,
    hasHeliborneInf: allProfiles.filter((p) => p.infantry.heliborneCardCount > 0).length / total,
    hasActiveProtection: allProfiles.filter((p) => p.tanks.activeProtectionCardCount > 0).length / total,
  };

  return {
    sortedValues,
    binaryRates,
    ...(aamPlaneRangeBenchmark?.length ? { aamPlaneRangeBenchmark } : {}),
  };
}
